import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewChild,
  ViewChildren,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatSidenav,
  MatSidenavContent,
  MatSidenavModule,
} from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
  NavigationEnd,
} from '@angular/router';

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  BreakpointObserver,
  Breakpoints,
  MediaMatcher,
} from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { IMAGES } from '@orderna/admin-panel/src/app/common/constants/images';
import { SubscriptionManager } from '@orderna/admin-panel/src/utils/subscription-manager';
import { map, takeUntil, filter, combineLatest } from 'rxjs';
import { HasRoleDirective } from '../../common/directives/has-role.directive';
import { AuthApiService } from '../../core/auth/auth-api.service';
import { SIDE_MENU } from '../../data/side-menu';
import { CurrentTransactionService } from '../../services/shared/current-transaction/current-transaction.service';
import { MenuControlsService } from '../../services/shared/menu-controls/menu-controls.service';
import { RegisterSelectorService } from '../../services/shared/register-selector/register-selector.service';
import { SidebarService } from '../../services/shared/sidebar/sidebar.service';
import { StoreSelectorService } from '../../services/shared/store-selector/store-selector.service';
import { SkeletonComponent } from '../../shared/components/skeleton/skeleton.component';
import { openSubscriptionRequiredModal } from '../../features/profile/components/modals/subscription-required/subscription-required-modal.component';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { Role } from '../../model/enum/role';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TransactionControlsSkeletonComponent } from '../../features/registers/point-of-sales/components/transaction-controls/transaction-controls-skeleton.component';
import { TransactionControlsComponent } from '../../features/registers/point-of-sales/components/transaction-controls/transaction-controls.component';
import { OfflineService } from '../../services/shared/offline/offline.service';
import { NotificationPanelTriggerComponent } from '../../features/notifications/components/notification-panel-trigger/notification-panel-trigger.component';
import { PosDataPreloaderService } from '../../services/shared/offline/pos-data-preloader.service';
import packageJson from '../../../../package.json';
import { blurActiveElement } from '@orderna/admin-panel/src/utils/blur-active-element';
import { waitForSidebarAnimationToComplete } from '@orderna/admin-panel/src/utils/wait-for-sidebar-animation-to-complete';
import { MenuItem } from '../../model/menu-item';
import { CurrentDeviceService } from '../../services/shared/current-device/current-device.service';
import { CurrentRegisterSessionService } from '../../services/shared/current-register-session/current-register-session.service';

@Component({
  selector: 'app-default-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatMenuModule,
    MatExpansionModule,
    MatTooltipModule,
    SkeletonComponent,
    HasRoleDirective,
    TransactionControlsComponent,
    TransactionControlsSkeletonComponent,
    NotificationPanelTriggerComponent,
  ],
  templateUrl: './default-layout.component.html',
  styleUrl: './default-layout.component.scss',
  animations: [
    trigger('onSidenavChange', [
      state('mini-collapsed', style({ 'margin-left': '65px' })),
      state('mini-expanded', style({ 'margin-left': '255px' })),
      state('full', style({ 'margin-left': '255px' })),
      state('mobile', style({ 'margin-left': '0' })),
      state('hidden', style({ 'margin-left': '0' })),

      transition('* <=> *', animate('250ms ease-in')),
    ]),
  ],
})
export class DefaultLayout extends SubscriptionManager implements OnDestroy {
  version = packageJson.staticVersion;

  IMAGES = IMAGES;

  @ViewChild('snav') sidenav!: MatSidenav;
  @ViewChild(MatSidenavContent) sidenavContent!: MatSidenavContent;
  @ViewChildren(MatExpansionPanel) panels!: MatExpansionPanel[];

  // Sidenav
  #sidebarService = inject(SidebarService);

  sidenavMode = this.#sidebarService.mode;
  isFull = this.#sidebarService.isFull;
  isMobile = this.#sidebarService.isMobile;
  isMiniExpanded = this.#sidebarService.isMiniExpanded;
  isMiniCollapsed = this.#sidebarService.isMiniCollapsed;
  isMini = this.#sidebarService.isMini;
  isSidenavExpanded = this.#sidebarService.isExpanded;

  hasExpandableItems = computed(() => {
    const role = this.currentUser()?.role;

    return this.sidenavItems().some(
      (item) =>
        (!role || item.allowedRoles.includes(role)) && !!item.children?.length,
    );
  });
  footerPaddingClass = computed(() => {
    if (this.isHidden() || this.isMobile()) {
      return 'pl-0';
    }

    return this.isMiniCollapsed() ? 'pl-[65px]' : 'pl-[255px]';
  });

  sidenavHeaderClass = computed(() =>
    this.isMiniCollapsed() ? 'justify-center' : 'justify-start',
  );

  sidenavLogoWrapperClass = computed(() =>
    this.isMiniCollapsed()
      ? 'flex flex-col items-center justify-center gap-1'
      : 'relative',
  );

  sidenavVersionClass = computed(() => {
    if (this.isMiniCollapsed()) return '';
    if (this.isMobile()) return 'absolute -top-1 -right-3';
    return 'absolute top-1 -right-7';
  });

  mobileQuery: MediaQueryList;
  sidenavItems = computed(() => {
    const isOffline = this.#offlineService.isOffline();
    const hasFnbStore = this.hasFnbStore();
    const hasRetailStore = this.hasRetailStore();

    const isMenuItemVisible = (
      item: MenuItem,
      isOffline: boolean,
      hasFnbStore: boolean,
      hasRetailStore: boolean,
    ) => {
      if (isOffline && !item.allowedOffline) {
        return false;
      }

      if (item.requiresFnbStore && !hasFnbStore) {
        return false;
      }

      if (item.requiresRetailStore && !hasRetailStore) {
        return false;
      }

      return true;
    };

    return (
      SIDE_MENU
        // Apply visibility rules to child menu items
        .map((item) => ({
          ...item,
          children: item.children.filter((child) =>
            isMenuItemVisible(child, isOffline, hasFnbStore, hasRetailStore),
          ),
        }))
        // Apply visibility rules to parent menu items
        .filter((item) =>
          isMenuItemVisible(item, isOffline, hasFnbStore, hasRetailStore),
        )
    );
  });

  private _mobileQueryListener: () => void;
  private fullscreenHandler!: () => void;

  breakpoints$ = this.breakpointObserver
    .observe([
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge,
    ])
    .pipe(map((result) => result?.breakpoints));
  route$ = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
  );

  // Injectables
  #authService = inject(AuthApiService);
  #menuControlsService = inject(MenuControlsService);
  #storeSelectorService = inject(StoreSelectorService);
  #registerSelectorService = inject(RegisterSelectorService);
  #currentTransactionService = inject(CurrentTransactionService);
  #offlineService = inject(OfflineService);
  #posDataPreloader = inject(PosDataPreloaderService);
  #currentDevice = inject(CurrentDeviceService);
  #currentRegisterSession = inject(CurrentRegisterSessionService);
  isOffline = this.#offlineService.isOffline;

  // Signals
  currentUrl = signal('');

  // Computed
  currentUser = this.#authService.currentUser;
  isHidden = this.#sidebarService.isHidden;
  isLiveOrdersPage = computed(() =>
    this.currentUrl().includes('/kitchen/live-orders'),
  );
  isPosPage = computed(() =>
    this.currentUrl().includes('/registers/point-of-sales'),
  );

  hasFnbStore = this.#storeSelectorService.hasFnbStore;
  hasRetailStore = this.#storeSelectorService.hasRetailStore;
  registersLoading = this.#registerSelectorService.loading;
  storesLoading = this.#storeSelectorService.loading;
  storeName = computed(() => this.#currentDevice.store()?.name);
  registerName = computed(() => this.#currentDevice.register()?.label);

  isDisplayCurrentStoreAndRegister =
    this.#menuControlsService.isDisplayCurrentStoreAndRegister;
  isDisplayManageRegisterButton =
    this.#menuControlsService.isDisplayManageRegisterButton;
  isDisplayBackToPosButton = this.#menuControlsService.isDisplayBackToPosButton;
  isDisplayTransactionControls =
    this.#menuControlsService.isDisplayTransactionControls;

  transactionSize = computed(
    () => this.#currentTransactionService.items().size,
  );

  pageData = computed(() => ({
    currentUser: this.currentUser(),
    isLoading:
      !this.currentUser() ||
      (this.isDisplayCurrentStoreAndRegister() &&
        (this.storesLoading() || this.registersLoading())),
  }));

  constructor(
    private router: Router,
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private breakpointObserver: BreakpointObserver,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
  ) {
    super();
    combineLatest([this.breakpoints$, this.route$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([breakpoints]) => {
        for (const query of Object.keys(breakpoints)) {
          if (breakpoints[query]) {
            switch (query) {
              case Breakpoints.XSmall:
              case Breakpoints.Small:
                this.#sidebarService.setMode('mobile');
                break;
              case Breakpoints.Medium:
                this.#sidebarService.setMode('mini-expanded');
                break;
              case Breakpoints.Large:
              case Breakpoints.XLarge:
                if (
                  this.router.url === '/registers/point-of-sales' ||
                  this.router.url === '/dashboard'
                ) {
                  this.#sidebarService.setMode('mini-collapsed');
                  break;
                }

                this.#sidebarService.setMode('full');

                break;
              default:
                this.#sidebarService.setMode('mobile');
            }
          }
        }
      });

    this.mobileQuery = media.matchMedia('(max-width: 960px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addEventListener('change', this._mobileQueryListener);

    this.#posDataPreloader.preloadAllCriticalData().catch(() => {});
  }

  async ngOnInit(): Promise<void> {
    this.initCurrentUrl();
    this.initCurrentUser();
    this.checkForPaymentOverdue();

    await this.#currentRegisterSession.init();

    this.initFullscreenListener();
    this.initIsFullscreen();
  }


  private initCurrentUrl() {
    this.currentUrl.set(this.router.url);
    this.route$.subscribe(() => {
      this.currentUrl.set(this.router.url);
    });
  }

  private initCurrentUser(): void {
    this.#authService.init();
  }

  private checkForPaymentOverdue(): void {
    this.#authService.currentUser$.subscribe((user) => {
      if (!user) return;

      const { hasActiveSubscription, email, role } = user;
      const shouldShowOverdueModal =
        environment.subscriptionModalEnabled &&
        role !== Role.SUPER_ADMIN &&
        !hasActiveSubscription;

      if (!shouldShowOverdueModal) return;

      openSubscriptionRequiredModal(this.dialog, {
        email,
        daysOverdue: 0, // TODO: Replace with actual overdue days
      });
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
    if (this.fullscreenHandler) {
      document.removeEventListener('fullscreenchange', this.fullscreenHandler);
    }

    // Order matters
    this.#currentTransactionService.reset();
    this.#currentRegisterSession.reset();
    this.#registerSelectorService.reset();
    this.#storeSelectorService.reset();
    this.#currentDevice.reset();
  }

  toggleMini(): void {
    if (this.isMiniExpanded()) {
      this.collapseMini();
    } else {
      this.expandMini();
    }
  }

  private expandMini(): void {
    this.#sidebarService.setMode('mini-expanded');
  }

  private collapseMini(): void {
    this.#sidebarService.setMode('mini-collapsed');
    this.closeExpansionPanels();
  }

  handleSignout() {
    this.#authService.logOut();
  }

  getRouterUrl(): String {
    return this.router.url;
  }

  refreshExpansionPanels() {
    if (!this.panels) {
      console.error('No panels to refresh');
    }

    for (const panel of this.panels) {
      panel.expanded = false;
    }
  }

  onExpansionPanelOpened() {
    if (this.isMiniCollapsed()) {
      this.expandMini();
    }
  }

  closeExpansionPanels() {
    if (!this.panels) {
      console.error('No panels to close');
    }

    for (const panel of this.panels) {
      panel.expanded = false;
    }
  }

  goToProfilePage() {
    this.router.navigate(['/profile']);
  }

  goToNotificationsPage() {
    this.router.navigate(['/notifications']);
  }

  getLogoSource(): string {
    return this.isMiniCollapsed()
      ? IMAGES.logoIcon.src
      : IMAGES.logoOnDarkBg.src;
  }

  getLogoAlt(): string {
    return this.isMiniCollapsed()
      ? IMAGES.logoIcon.alt
      : IMAGES.logoOnDarkBg.alt;
  }

  getLogoWidth(): string {
    return this.isMiniCollapsed() ? '40' : '160';
  }

  getLogoMargin(): string {
    if (this.isMiniCollapsed()) {
      return '0 auto';
    }
    return this.sidenavMode() === 'mobile' ? 'auto' : '11px';
  }

  // Fullscreen-related methods
  async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) {
        await this.exitFullscreen();
      } else {
        await this.enterFullscreen();
      }
    } catch (err) {
      this.#sidebarService.setVisibility(true);
      console.error('Error toggling fullscreen:', err);
    }
  }

  private initIsFullscreen(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(async () => {
        try {
          let currentRoute = this.activatedRoute;

          while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
          }

          const isFullscreenRoute =
            currentRoute.snapshot.data['fullscreen'] === true;

          if (isFullscreenRoute && !document.fullscreenElement) {
            await this.enterFullscreen();
          } else if (!isFullscreenRoute && document.fullscreenElement) {
            await this.exitFullscreen();
          }
        } catch (err) {
          console.error('Error syncing fullscreen state:', err);
        }
      });
  }

  private initFullscreenListener(): void {
    this.fullscreenHandler = () => {
      const isFullscreen = !!document.fullscreenElement;

      this.#sidebarService.setVisibility(!isFullscreen);

      if (!isFullscreen && this.isMiniCollapsed()) {
        this.closeExpansionPanels();
      }
    };

    document.addEventListener('fullscreenchange', this.fullscreenHandler);
  }

  private async enterFullscreen(): Promise<void> {
    this.#sidebarService.setVisibility(false);

    blurActiveElement();
    await waitForSidebarAnimationToComplete();

    await document.documentElement.requestFullscreen();
  }

  private async exitFullscreen(): Promise<void> {
    blurActiveElement();
    await document.exitFullscreen();
  }
}
